import React, { useMemo } from "react";
import CardHeader from "../Common/CardHeader";

/**
 * FinanceCardHead
 * Reusable finance card shell:
 * - Header row (title + right)
 * - Optional headline value + subtitle
 * - Body renders children
 *
 * Uses your global theme classes:
 * yx-card, yx-card-head, yx-card-body, yx-kicker, yx-value, yx-sub, yx-pill, yx-divider
 *
 * If you haven't added them yet, keep the classNames and style via your global CSS file.
 */
const FinanceCardHead = ({
  title = "Card title",
  right = null,                // e.g. <span className="yx-pill yx-pill--muted">As of today ...</span>
  value = null,                // number or string (already formatted or raw)
  currency = "",               // e.g. "UGX"
  formatValue = null,          // optional formatter (v)=>string
  negativeParen = true,        // wrap negatives like (1,000)
  isNegative = null,           // optional explicit negative (overrides auto)
  subtitle = "",               // e.g. "Business net worth"
  children,
  className = "",
  headClassName = "",
  bodyClassName = "",
  styles = null,
}) => {
  const resolvedValue = useMemo(() => {
    if (value === null || value === undefined || value === "") return null;

    const n = typeof value === "number" ? value : Number(value);
    const canParse = !Number.isNaN(n) && value !== true && value !== false;

    // if caller provided a formatter, use it
    if (typeof formatValue === "function") {
      return formatValue(value);
    }

    // default formatting: show currency + local formatting if numeric
    if (canParse) {
      const abs = Math.abs(n).toLocaleString(undefined, { maximumFractionDigits: 0 });
      if (n < 0 && negativeParen) return `${currency} (${abs})`;
      return `${currency} ${abs}`;
    }

    // non-numeric: render as-is (still prefix currency if provided)
    return currency ? `${currency} ${String(value)}` : String(value);
  }, [value, currency, formatValue, negativeParen]);

  const neg = useMemo(() => {
    if (isNegative !== null && isNegative !== undefined) return !!isNegative;
    const n = typeof value === "number" ? value : Number(value);
    return !Number.isNaN(n) && n < 0;
  }, [value, isNegative]);

  const RightNode =
    right || (<></>);

  return (
    <div className={`yx-card ${className}`} style={styles}>
      <div className={`yx-card-head ${headClassName}`}>
        <CardHeader title={title} right={RightNode} />

        {resolvedValue !== null && (
          <div className={`yx-value ${neg ? "is-negative" : ""}`} style={{ marginTop: 10 }}>
            {resolvedValue}
          </div>
        )}

        {subtitle ? (
          <div className="yx-sub" style={{ marginTop: 6 }}>
            {subtitle}
          </div>
        ) : null}
      </div>

      <div className={`yx-card-body ${bodyClassName}`}>
        {children}
      </div>
    </div>
  );
};

export default FinanceCardHead;
